import { Profile } from 'src/app/types/Profile';
import {
  VendorBankingUiMode,
  VendorUiCapability,
} from 'src/app/types/VendorUi';

function normalizeStatus(status?: string): string {
  return status?.trim().toUpperCase() ?? '';
}

function isEmptyVbanBankDetails(
  vbanBankDetailsByCurrency?: Record<string, unknown[]>,
): boolean {
  if (!vbanBankDetailsByCurrency) {
    return true;
  }

  const currenciesWithDetails = Object.entries(
    vbanBankDetailsByCurrency,
  ).filter(([, details]) => Array.isArray(details) && details.length > 0);

  return currenciesWithDetails.length === 0;
}

function hasOnlyUsdVbanBankDetails(
  vbanBankDetailsByCurrency?: Record<string, unknown[]>,
): boolean {
  if (!vbanBankDetailsByCurrency) {
    return false;
  }

  const currenciesWithDetails = Object.entries(vbanBankDetailsByCurrency)
    .filter(([, details]) => Array.isArray(details) && details.length > 0)
    .map(([currency]) => currency.toLowerCase());

  return (
    currenciesWithDetails.length === 1 && currenciesWithDetails[0] === 'usd'
  );
}

function isMultiCurrencyActivated(
  profile: Profile | null | undefined,
): boolean {
  return (
    normalizeStatus(profile?.multiCurrencyStatus) === 'ACTIVATED' ||
    profile?.multiCurrencyActivated === true
  );
}

function isVendorVbanActivated(profile: Profile | null | undefined): boolean {
  return normalizeStatus(profile?.vendorVbanStatus) === 'ACTIVATED';
}

export function resolveVendorBankingUiMode(
  profile: Profile | null | undefined,
): VendorBankingUiMode {
  const vendorVbanStatus = normalizeStatus(profile?.vendorVbanStatus);
  const multiCurrencyStatus = normalizeStatus(profile?.multiCurrencyStatus);

  if (
    isEmptyVbanBankDetails(profile?.vbanBankDetailsByCurrency) &&
    vendorVbanStatus === 'PENDING' &&
    multiCurrencyStatus === 'PENDING'
  ) {
    return VendorBankingUiMode.LegacyPending;
  }

  // Pure new-user path: vendor VBAN activated, USD-only, multi-currency not yet enabled.
  if (
    hasOnlyUsdVbanBankDetails(profile?.vbanBankDetailsByCurrency) &&
    vendorVbanStatus === 'ACTIVATED' &&
    multiCurrencyStatus === 'PENDING'
  ) {
    return VendorBankingUiMode.UsdVbanActive;
  }

  // Legacy migration (USD client pools) — not once multi-currency is on vendor wallet.
  if (
    profile?.partnerVbanExists === true &&
    !isEmptyVbanBankDetails(profile?.vbanBankDetailsByCurrency) &&
    isMultiCurrencyActivated(profile)
  ) {
    return VendorBankingUiMode.LegacyWithVendorVban;
  }

  return VendorBankingUiMode.MultiCurrency;
}

/** Legacy onboarding: USD-only invoicing UI (no VBAN/multi-currency yet). */
export function isLegacyVendorBillingProfile(
  profile: Profile | null | undefined,
): boolean {
  return (
    resolveVendorBankingUiMode(profile) === VendorBankingUiMode.LegacyPending
  );
}

export function isVendorUiCapabilityVisible(
  capability: VendorUiCapability,
  profile: Profile | null | undefined,
): boolean {
  const mode = resolveVendorBankingUiMode(profile);

  switch (capability) {
    case 'currencySelector':
      // Show the interactive multi-currency selector for MultiCurrency users, and
      // also for LegacyWithVendorVban users once they activate multi-currency.
      if (mode === VendorBankingUiMode.MultiCurrency) {
        return true;
      }
      if (mode === VendorBankingUiMode.LegacyWithVendorVban) {
        return isMultiCurrencyActivated(profile);
      }
      return false;

    case 'multiCurrencyEnableCta':
      if (mode === VendorBankingUiMode.LegacyPending) {
        return false;
      }
      if (mode === VendorBankingUiMode.UsdVbanActive) {
        return true;
      }
      // LegacyWithVendorVban and MultiCurrency: show CTA until MC is activated.
      return !isMultiCurrencyActivated(profile);

    case 'multiCurrencyBanner':
      return isVendorUiCapabilityVisible('multiCurrencyEnableCta', profile);

    case 'bankDetailsDialog':
      if (mode === VendorBankingUiMode.LegacyPending) {
        return false;
      }
      // UsdVbanActive and LegacyWithVendorVban both have VBAN — always show.
      if (
        mode === VendorBankingUiMode.UsdVbanActive ||
        mode === VendorBankingUiMode.LegacyWithVendorVban
      ) {
        return true;
      }
      return (profile?.vbanCurrencyOptions?.length ?? 0) > 0;

    case 'addCurrencyCta':
      // Available once multi-currency is activated, for both MultiCurrency and
      // LegacyWithVendorVban users.
      return (
        (mode === VendorBankingUiMode.MultiCurrency ||
          mode === VendorBankingUiMode.LegacyWithVendorVban) &&
        isMultiCurrencyActivated(profile)
      );

    case 'autoWithdrawalSettings':
      // LegacyWithVendorVban has VBAN — no auto-withdrawal settings.
      if (mode === VendorBankingUiMode.LegacyWithVendorVban) {
        return false;
      }
      return (
        !isVendorVbanActivated(profile) && !isMultiCurrencyActivated(profile)
      );

    default:
      return true;
  }
}

export function shouldForceUsdCurrencySelection(
  profile: Profile | null | undefined,
): boolean {
  const mode = resolveVendorBankingUiMode(profile);
  if (
    mode === VendorBankingUiMode.LegacyPending ||
    mode === VendorBankingUiMode.UsdVbanActive
  ) {
    return true;
  }
  // LegacyWithVendorVban users are USD-only until they activate multi-currency.
  if (mode === VendorBankingUiMode.LegacyWithVendorVban) {
    return !isMultiCurrencyActivated(profile);
  }
  return false;
}
